import type { AirRateQuote } from '@aircargomcp/types'
import { fmtMoney, fmtNum } from '../lib/format'

// The "rate terminal" readout: mono figures, cobalt all-in, weight band + surcharge breakdown.
export function RateQuoteCard({ q }: { q: AirRateQuote }) {
  return (
    <div className="card overflow-hidden">
      {/* lane header */}
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="mono text-2xl font-extrabold tracking-tight text-ink tnum">
            {q.origin}
          </span>
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            className="text-signal"
            fill="currentColor"
            aria-hidden
          >
            <path d="M22 16.5 14 13V6.5a2 2 0 0 0-4 0V13l-8 3.5V18l8-2.2V20l-2 1.3V22l3.5-1 3.5 1v-.7L14 20v-4.2l8 2.2v-1.5Z" />
          </svg>
          <span className="mono text-2xl font-extrabold tracking-tight text-ink tnum">
            {q.destination}
          </span>
        </div>
        <span className="mono rounded-full border border-border px-2.5 py-1 text-[11px] uppercase tracking-wider text-faint">
          {q.currency}
        </span>
      </div>

      {/* the all-in headline figure */}
      <div className="grid grid-cols-2 gap-px bg-border">
        <div className="bg-surface-0 px-5 py-5">
          <div className="mono text-[10px] uppercase tracking-wider text-faint">All-in / kg</div>
          <div className="mono mt-1 text-3xl font-extrabold text-signal tnum">
            {fmtMoney(q.all_in_per_kg, q.currency)}
          </div>
        </div>
        <div className="bg-surface-0 px-5 py-5 text-right">
          <div className="mono text-[10px] uppercase tracking-wider text-faint">Total charge</div>
          <div className="mono mt-1 text-3xl font-extrabold text-ink tnum">
            {fmtMoney(q.total_charge, q.currency)}
          </div>
        </div>
      </div>

      {/* breakdown */}
      <dl className="divide-y divide-border px-5 py-1 text-sm">
        {[
          ['Chargeable weight', `${fmtNum(q.chargeable_weight_kg, 0)} kg`],
          ['Applied weight band', q.applied_band],
          ['Net rate / kg', fmtMoney(q.net_per_kg, q.currency)],
          ['Surcharge / kg (FSC + SSC)', fmtMoney(q.surcharge_per_kg, q.currency)],
          ...(q.airline_iata ? [['Carrier', q.airline_iata] as [string, string]] : []),
          ['Source', q.source],
        ].map(([k, v]) => (
          <div key={k} className="flex items-center justify-between py-2.5">
            <dt className="text-muted">{k}</dt>
            <dd className="mono font-semibold text-ink tnum">{v}</dd>
          </div>
        ))}
      </dl>

      <div className="border-t border-border bg-surface-1/50 px-5 py-3">
        <p className="text-[11px] leading-relaxed text-faint">
          Total = max(min charge, chargeable weight × all-in per-kg). Chargeable weight is the
          greater of gross and volumetric (volume ÷ 6000).
        </p>
      </div>
    </div>
  )
}
